import { normalizeApiError, getAuthToken, setAuthToken } from '@/lib/authApi';
import { normalizeApiError as structuredApiError } from '@/lib/apiError';
import { healthUrl } from '@/lib/apiOrigin';
// geo-api client — the ONLY channel through which the pilot portal talks to
// business workflows. No Supabase business calls in the frontend. No API keys.
// The bearer access token is the one authApi holds IN MEMORY (never
// localStorage); the HttpOnly refresh cookie travels on credentialed requests.
// Base URL, timeout, and environment label are environment-driven.
//
// PRODUCTION PRESERVATION:
//  - Honors VITE_GEO_API_BASE_URL first, then falls back to VITE_API_BASE_URL.
//  - Retains the canonical trailing `/api` in production.
//  - Service methods append resource paths such as `/investor/profile`.
//  - Final production requests must resolve under:
//      https://api.giisdec.com/api/...

type ImportMetaEnv = {
  VITE_GEO_API_BASE_URL?: string;
  VITE_API_BASE_URL?: string;
  VITE_GEO_API_TIMEOUT_MS?: string;
  VITE_APP_ENV?: 'development' | 'staging' | 'production';
};

const env: ImportMetaEnv = ((import.meta as unknown) as { env?: ImportMetaEnv }).env || {};

function normalizeBase(raw: string | undefined, fallback: string): string {
  const candidate = (raw || fallback).trim();
  return candidate.replace(/\/+$/, '');
}

// Canonical portal API base.
//
// Production:
//   VITE_GEO_API_BASE_URL=https://api.giisdec.com/api
//   VITE_API_BASE_URL=https://api.giisdec.com/api
//
// Service paths already begin with resources such as:
//   /investor/profile
//   /payments
//   /geo/...
//
// Therefore BASE_URL must retain the configured trailing /api.
// Do not strip /api and do not append another /api segment here.
const BASE_URL = normalizeBase(
  env.VITE_GEO_API_BASE_URL || env.VITE_API_BASE_URL,
  '/api'
);

const TIMEOUT_MS = Number(env.VITE_GEO_API_TIMEOUT_MS || 15000);
export const APP_ENV: 'development' | 'staging' | 'production' =
  env.VITE_APP_ENV || 'production';

// The access token is owned by authApi (memory only). geoApi reads the same
// in-memory credential so /geo/* and /investor/* calls authenticate, and never
// persists it to a JS-readable store.
export function getGeoToken(): string | null {
  return getAuthToken();
}

export function setGeoToken(token: string | null): void {
  setAuthToken(token);
}

export interface GeoApiResult<T> {
  data: T | null;
  error: string | null;
  status: number;
  /** P9E: the backend error code on a failed response (e.g. INVESTOR_PROFILE_REQUIRED). */
  code?: string | null;
}

async function request<T>(
  path: string,
  init: RequestInit = {}
): Promise<GeoApiResult<T>> {
  const token = getGeoToken();
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  const suppliedHeaders =
    (init.headers as Record<string, string | undefined> | undefined) || {};

  const isFormData =
    typeof FormData !== 'undefined' && init.body instanceof FormData;

  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  for (const [key, value] of Object.entries(suppliedHeaders)) {
    if (value !== undefined) headers[key] = value;
  }

  if (token) headers.Authorization = `Bearer ${token}`;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      ...init,
      headers,
      // Send the HttpOnly refresh cookie so a 401 can be recovered by a
      // credentialed /api/auth/refresh without any JS-held refresh token.
      credentials: 'include',
      signal: controller.signal,
    });
    const isJson = res.headers.get('content-type')?.includes('application/json');
    const body = isJson ? await res.json().catch(() => null) : null;

    if (!res.ok) {
      // Auto sign-out on 401 so ProtectedRoute can redirect.
      if (res.status === 401) setGeoToken(null);
      return {
        data: null,
        error: normalizeApiError(body, res.status),
        status: res.status,
        code: structuredApiError(body, res.status).code,
      };
    }
    return { data: (body as T) ?? null, error: null, status: res.status };
  } catch (err) {
    const aborted = (err as { name?: string })?.name === 'AbortError';
    return {
      data: null,
      error: aborted
        ? 'Request timed out. Please try again.'
        : err instanceof Error
        ? err.message
        : 'Network error',
      status: 0,
    };
  } finally {
    clearTimeout(timer);
  }
}

export const geoApi = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  postForm: <T>(path: string, body: FormData) =>
    request<T>(path, {
      method: 'POST',
      body,
    }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  baseUrl: BASE_URL,
  env: APP_ENV,
};

/**
 * R11P P0.6-NITA-02A (R6) — GET <API origin>/health, the Geological API's
 * liveness probe. The configured base is the /api prefix and there is no
 * /api/health, so the URL is derived from the origin (apiOrigin.healthUrl).
 * Returns the HTTP status, or 0 on network failure; callers treat only 2xx as
 * available (apiOrigin.isProbeHealthy). Unauthenticated and uncached.
 */
export async function probeApiLiveness(): Promise<number> {
  try {
    const res = await fetch(healthUrl(BASE_URL), { method: 'GET', credentials: 'omit', cache: 'no-store' });
    return res.status;
  } catch {
    return 0;
  }
}
